import React from 'react'
import {
  Text as ReactNativeText,
  Platform,
  Linking,
  TextProps as RNTextProps,
  TextStyle,
} from 'react-native'
import { flatten, mergeAll } from 'ramda'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import Hyperlink from 'react-native-hyperlink'
import { color } from '../../theme'
import { typography } from '../../theme/typography'
import { useStores } from '@trivie/core'

export interface TextProps extends RNTextProps {
  /**
   * Children components.
   */
  children?: React.ReactNode

  /**
   * Text which is looked up via i18n.
   */
  tx?: string

  /**
   * Optional options to pass to i18n. Useful for interpolation
   * as well as explicitly setting locale or translation fallbacks.
   */
  txOptions?: object

  /**
   * The text to display if not using `tx` or nested components.
   */
  text?: string | undefined | null

  /**
   * An optional style override useful for padding & margin.
   */
  style?: TextStyle | TextStyle[]

  /**
   * One of the different types of text presets.
   */
  variant?: keyof typeof typography

  disableLink?: boolean

  uppercase?: boolean

  /**
   * Text alignment
   * @default left
   */
  align?: 'auto' | 'center' | 'justify' | 'left' | 'right'
}

/**
 * HOC wrapping the default react-native Text component
 *
 * Supports linking, localization, and typography variants
 */
export const Text = observer((props: TextProps) => {
  // grab the props
  const {
    align = 'left',
    variant = 'body1',
    uppercase = false,
    tx,
    txOptions,
    text,
    children,
    style: styleOverride,
    allowFontScaling = false,
    disableLink,
    ...rest
  } = props

  const { t } = useTranslation()

  // @ts-ignore
  const i18nText = tx && t(tx, txOptions)

  const content = i18nText || text || children

  const { uiStore } = useStores()

  const handleOnPressLink = (url: string) => {
    if (Platform.OS === 'web') {
      window.open(url, '_blank')
    } else {
      Linking.openURL(url)
    }
  }

  const style = mergeAll(
    flatten([typography[variant] || typography.body1, { textAlign: align }, styleOverride]),
  ) as object[]

  const androidWeightMap = {
    '800': 'Inter-ExtraBold',
    '700': 'Inter-Bold',
    '600': 'Inter-SemiBold',
    '500': 'Inter-Medium',
    '400': 'Inter-Regular',
    '300': 'Inter-Thin',
  }

  return disableLink ? (
    <ReactNativeText
      allowFontScaling={allowFontScaling}
      style={{
        ...style,
        fontFamily:
          uiStore?.branding?.font_family !== 'Inter'
            ? uiStore?.branding?.font_family
            : Platform.OS === 'android'
              ? androidWeightMap[typography[variant].fontWeight]
              : typography.fontFamily,
        fontWeight: typography[variant].fontWeight,
      }}
      {...rest}
    >
      {uppercase ? String(content)?.toLocaleUpperCase() : content ?? ''}
    </ReactNativeText>
  ) : (
    <Hyperlink
      linkStyle={{ color: color.blue, paddingHorizontal: 0 }}
      onPress={(url: string) => handleOnPressLink(url)}
    >
      <ReactNativeText
        allowFontScaling={allowFontScaling}
        style={{
          ...style,
          fontFamily:
            uiStore?.branding?.font_family !== 'Inter'
              ? uiStore?.branding?.font_family
              : Platform.OS === 'android'
                ? androidWeightMap[typography[variant].fontWeight]
                : typography.fontFamily,
          fontWeight: typography[variant]?.fontWeight,
        }}
        {...rest}
      >
        {uppercase ? String(content)?.toLocaleUpperCase() : content ?? ''}
      </ReactNativeText>
    </Hyperlink>
  )
})
